/**
 * Leave **approvals** — the second half of `/hr/leave`, WP-W7.
 * `web/shapes/hr-leave.md` §6 is the agreed shape.
 *
 * Rendered only for a session holding `hr.approve_leave` (`LeaveScreen` decides that,
 * the same client-side honesty gate `AttendanceScreen`'s shift-management subsection and
 * `InterviewsScreen`'s `canViewAll` already use — never the authorisation, which the
 * server still runs on every call).
 *
 * There is no company-wide "pending requests" endpoint, so the approver **chooses an
 * employee** and reads that employee's `pending` requests. It was a typed-id box under
 * a line promising a picker "sẽ có sau" until the finish review (M2): `EmployeePicker`
 * has been mounted since Task T2-B, `GET /employees`'s own resolved scope decides who
 * appears in it, and an approver should not have to know a colleague's internal id by
 * heart to answer their leave request. Every `pending` row may be approved or
 * rejected; there is no illegal action to hide here, so unlike the offer/interview
 * status controls this is not a derived-transition table, just two buttons per row.
 *
 * The over-draw `409` (`WORKFORCE_CONFLICT`, `details.reason: 'insufficient_balance'`)
 * is read back and printed with its three numbers, beside the ordinary `ApiErrorNote`
 * sentence — the same "print the server's extra detail" instinct
 * `allowedTransitionsFromRefusal` applies to a `409 WORKFORCE_INVALID_STATE_TRANSITION`
 * on the offers screen.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatDay } from '../../i18n/datetime';
import type { LeaveRequestRecord, LeaveType } from '../../api/hr';
import {
  LEAVE_REQUEST_STATUS_BADGE,
  LEAVE_REQUEST_STATUS_LABEL,
  insufficientBalanceDetails,
} from '../../hr/leave';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { EmployeePicker, type EmployeePickerValue } from '../../components/EmployeePicker';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { SEVERITY_ICON } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';

export type LeaveDecisionAction = 'approve' | 'reject';

export interface LeaveApprovalsPanelProps {
  readonly employeeId: string | null;
  readonly onLoadEmployee: (id: string) => void;

  readonly types: readonly LeaveType[] | null;

  readonly listStatus: QueryStatus;
  readonly requests: readonly LeaveRequestRecord[] | null;
  readonly listError: WorkforceApiError | null;
  readonly onReload: () => void;

  /**
   * The request the last decision was aimed at — kept after the mutation settles, not
   * cleared to `null` on success or failure, exactly as the offers screen keeps
   * `actionOfferId` so a refusal stays attributed to its row. `decisionPending` is the
   * separate, genuinely in-flight fact: a row reads "deciding" only while both agree.
   */
  readonly decidingRequestId: string | null;
  readonly decisionPending: boolean;
  readonly decisionError: WorkforceApiError | null;
  readonly onDecide: (requestId: string, action: LeaveDecisionAction) => void;
}

export function LeaveApprovalsPanel(props: LeaveApprovalsPanelProps): ReactNode {
  const { t, locale } = useI18n();
  const [employee, setEmployee] = useState<EmployeePickerValue | null>(null);

  const typeName = new Map<string, string>();
  for (const type of props.types ?? []) typeName.set(type.id, type.name);

  return (
    <section
      className="wp-section"
      aria-labelledby="w7-approvals-heading"
      data-testid="w7-approvals"
    >
      <h2 className="wp-section__title" id="w7-approvals-heading">
        {t('hr.leave.approvals.title')}
      </h2>
      <p className="wp-small wp-muted">{t('hr.leave.approvals.intro')}</p>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (employee === null) return;
          props.onLoadEmployee(employee.id);
        }}
      >
        <div className="wp-filters__group">
          <EmployeePicker
            label={t('hr.employee.pickLabel')}
            testId="w7-approvals-employee"
            value={employee}
            onChange={setEmployee}
          />
          <p className="wp-small wp-muted">{t('hr.employee.pickHint')}</p>
        </div>
        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w7-approvals-load"
            disabled={employee === null}
          >
            {t('hr.leave.approvals.load')}
          </button>
        </div>
      </form>

      {props.employeeId === null ? (
        <EmptyState
          title={t('hr.leave.approvals.none.title')}
          description={t('hr.leave.approvals.none.body')}
          testId="w7-approvals-none"
        />
      ) : (
        <>
          {props.listStatus === 'loading' ? (
            <Skeleton
              rows={2}
              variant="block"
              label={t('hr.leave.approvals.subject')}
              testId="w7-approvals-skeleton"
            />
          ) : null}
          {props.listStatus === 'error' && props.listError !== null ? (
            <ApiErrorNote
              error={props.listError}
              onRetry={props.onReload}
              subject={t('hr.leave.approvals.subject')}
              testId="w7-approvals-error"
            />
          ) : null}

          {props.requests === null ? null : props.requests.length === 0 ? (
            <EmptyState
              title={t('hr.leave.approvals.empty.title')}
              description={t('hr.leave.approvals.empty.body')}
              testId="w7-approvals-empty"
            />
          ) : (
            <ul className="wp-rows" data-testid="w7-approvals-list">
              {props.requests.map((request) => {
                const badge = LEAVE_REQUEST_STATUS_BADGE[request.status];
                const deciding = props.decisionPending && props.decidingRequestId === request.id;
                // The last decision's refusal is attributed to the row it was aimed at,
                // exactly as the offers screen attributes `actionOfferId`'s error — not
                // reset to `null` on failure, only when a fresh decision starts elsewhere.
                const rowError =
                  props.decidingRequestId === request.id ? props.decisionError : null;
                const insufficient =
                  rowError === null ? null : insufficientBalanceDetails(rowError);
                return (
                  <li className="wp-row" key={request.id} data-testid={`w7-approval-${request.id}`}>
                    <div className="wp-row__main">
                      <p>
                        <Badge
                          severity={badge}
                          icon={SEVERITY_ICON[badge]}
                          label={t(LEAVE_REQUEST_STATUS_LABEL[request.status])}
                          testId={`w7-approval-status-${request.id}`}
                        />{' '}
                        <span>
                          {typeName.get(request.leave_type_id) ?? t('hr.leave.type.unknown')}
                        </span>
                      </p>
                      <p className="wp-small wp-muted">
                        {t('hr.leave.myRequests.row.meta', {
                          start: formatDay(request.start_date, locale, {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          }),
                          end: formatDay(request.end_date, locale, {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          }),
                          days: request.days,
                        })}
                      </p>
                      <p className="wp-small wp-muted">
                        {request.reason === null
                          ? t('hr.leave.myRequests.row.noReason')
                          : t('hr.leave.myRequests.row.reason', { reason: request.reason })}
                      </p>
                    </div>
                    <div className="wp-row__figure">
                      <div className="wp-toggle">
                        <button
                          className="wp-button wp-button--primary"
                          type="button"
                          data-testid={`w7-approve-${request.id}`}
                          disabled={deciding}
                          onClick={() => {
                            props.onDecide(request.id, 'approve');
                          }}
                        >
                          {deciding
                            ? t('hr.leave.approvals.deciding')
                            : t('hr.leave.approvals.approve')}
                        </button>
                        <button
                          className="wp-button"
                          type="button"
                          data-testid={`w7-reject-${request.id}`}
                          disabled={deciding}
                          onClick={() => {
                            props.onDecide(request.id, 'reject');
                          }}
                        >
                          {deciding
                            ? t('hr.leave.approvals.deciding')
                            : t('hr.leave.approvals.reject')}
                        </button>
                      </div>
                      {rowError === null ? null : (
                        <>
                          <ApiErrorNote
                            error={rowError}
                            subject={t('hr.leave.approvals.subject')}
                            testId={`w7-decision-error-${request.id}`}
                          />
                          {insufficient === null ? null : (
                            <p
                              className="wp-small"
                              data-testid={`w7-insufficient-balance-${request.id}`}
                            >
                              {t('hr.leave.approvals.insufficientBalance', {
                                accrued: insufficient.accruedDays,
                                used: insufficient.usedDays,
                                requested: insufficient.requestedDays,
                              })}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
